import type { ModelGeneration3DInput } from './__generated__/tasksStartModelGeneration3DMutation.graphql';
import type { ModelGeneration3DModel } from './types';

export type ModelGeneration3DParameter = ModelGeneration3DModel['supportedParameters'][number];
export type Model3DParameterValue = string | number | boolean | readonly number[] | null;
export type Model3DParameterValues = Partial<Record<string, Model3DParameterValue>>;
export type Model3DParameterSelections = Partial<Record<string, Model3DParameterValues>>;

export const DEFAULT_MODEL_3D_TASK_PARAMETERS = {
    faceCount: 50000,
    enablePBR: true
} satisfies Pick<ModelGeneration3DInput, 'faceCount' | 'enablePBR'>;

const MODEL_3D_TASK_PARAMETER_NAMES = new Set<string>([
    'faceCount',
    'enablePBR',
    'seed',
    'topology',
    'symmetryMode',
    'shouldRemesh',
    'shouldTexture',
    'poseMode',
    'texturePrompt',
    'textureImageUrl',
    'enableRigging',
    'riggingHeightMeters',
    'enableAnimation',
    'animationActionId',
    'enablePromptExpansion',
    'useOriginalAlpha',
    'geometryFileFormat',
    'material',
    'qualityMeshOption',
    'taPose',
    'bboxCondition',
    'addons',
    'previewRender'
]);

const INTEGER_PARAMETER_NAMES = new Set<string>(['faceCount', 'seed', 'animationActionId']);

const BOOLEAN_PARAMETER_NAMES = new Set<string>([
    'enablePBR',
    'shouldRemesh',
    'shouldTexture',
    'enableRigging',
    'enableAnimation',
    'enablePromptExpansion',
    'useOriginalAlpha',
    'taPose',
    'previewRender'
]);

const PARAMETER_LABELS: Record<string, string> = {
    faceCount: 'Face count',
    enablePBR: 'PBR materials',
    seed: 'Seed',
    topology: 'Topology',
    symmetryMode: 'Symmetry',
    shouldRemesh: 'Remesh',
    shouldTexture: 'Generate textures',
    poseMode: 'Pose',
    texturePrompt: 'Texture prompt',
    textureImageUrl: 'Texture image URL',
    enableRigging: 'Rigging',
    riggingHeightMeters: 'Rigging height',
    enableAnimation: 'Animation',
    animationActionId: 'Animation action',
    enablePromptExpansion: 'Prompt expansion',
    useOriginalAlpha: 'Use original alpha',
    geometryFileFormat: 'Geometry format',
    material: 'Material',
    qualityMeshOption: 'Quality and mesh',
    taPose: 'T/A-pose',
    bboxCondition: 'Bounding box',
    addons: 'Add-ons',
    previewRender: 'Preview render'
};

const normalizeType = (type: string | null | undefined) => (type ?? '').toLowerCase();

export const getModel3DParameterSelectionKey = (agentId: string, model: string) => `${agentId}:${model}`;

export const isModel3DTaskParameterName = (name: string) => MODEL_3D_TASK_PARAMETER_NAMES.has(name);

export const isModel3DBooleanParameter = (parameter: Pick<ModelGeneration3DParameter, 'name' | 'type'>) =>
    BOOLEAN_PARAMETER_NAMES.has(parameter.name) || normalizeType(parameter.type).includes('bool');

export const isModel3DIntegerParameter = (parameter: Pick<ModelGeneration3DParameter, 'name' | 'type'>) =>
    INTEGER_PARAMETER_NAMES.has(parameter.name) || normalizeType(parameter.type).includes('int');

export const isModel3DNumericParameter = (parameter: Pick<ModelGeneration3DParameter, 'name' | 'type'>) => {
    const type = normalizeType(parameter.type);
    return isModel3DIntegerParameter(parameter) || type.includes('decimal') || type.includes('float') || type.includes('double') || type.includes('number');
};

export const isModel3DArrayParameter = (parameter: Pick<ModelGeneration3DParameter, 'name' | 'type'>) =>
    parameter.name === 'bboxCondition' || normalizeType(parameter.type).includes('array') || normalizeType(parameter.type).includes('list');

export const getModel3DParameterLabel = (name: string) => {
    const knownLabel = PARAMETER_LABELS[name];
    if (knownLabel) return knownLabel;

    return name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase());
};

export const getSupportedModel3DParameters = (model: ModelGeneration3DModel | undefined, agentId: string | null | undefined) => {
    if (!model || !agentId) return [];

    return model.supportedParameters.filter((parameter) => {
        const supportsMode = parameter.supportedModes.length === 0 || parameter.supportedModes.includes(agentId);
        return supportsMode && isModel3DTaskParameterName(parameter.name);
    });
};

function parseNumberList(value: unknown): number[] | undefined {
    if (Array.isArray(value)) {
        const parsed = value.map((item) => Number(item)).filter((item) => Number.isFinite(item));
        return parsed.length > 0 ? parsed : undefined;
    }

    const text = String(value ?? '').trim();
    if (!text) return undefined;

    try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
            const numbers = parsed.map((item) => Number(item)).filter((item) => Number.isFinite(item));
            return numbers.length > 0 ? numbers : undefined;
        }
    } catch {
        // Fall back to comma/space separated values.
    }

    const numbers = text
        .split(/[,\s]+/)
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));

    return numbers.length > 0 ? numbers : undefined;
}

function parseBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value;
    if (value === null || value === undefined || value === '') return undefined;

    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    return undefined;
}

function clampNumber(value: number, parameter: ModelGeneration3DParameter) {
    let next = value;
    if (parameter.minValue !== null && parameter.minValue !== undefined) {
        next = Math.max(next, parameter.minValue);
    }
    if (parameter.maxValue !== null && parameter.maxValue !== undefined) {
        next = Math.min(next, parameter.maxValue);
    }
    return isModel3DIntegerParameter(parameter) ? Math.round(next) : next;
}

export function coerceModel3DParameterValue(parameter: ModelGeneration3DParameter, value: Model3DParameterValue | undefined) {
    if (value === null || value === undefined || value === '') return undefined;

    if (isModel3DArrayParameter(parameter)) {
        return parseNumberList(value);
    }

    if (isModel3DBooleanParameter(parameter)) {
        return parseBoolean(value);
    }

    if (isModel3DNumericParameter(parameter)) {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? clampNumber(numberValue, parameter) : undefined;
    }

    return String(value);
}

export function getDefaultModel3DParameterValue(parameter: ModelGeneration3DParameter): Model3DParameterValue | undefined {
    if (parameter.defaultValue !== null && parameter.defaultValue !== undefined && parameter.defaultValue !== '') {
        return coerceModel3DParameterValue(parameter, parameter.defaultValue);
    }

    if (parameter.name === 'faceCount') return DEFAULT_MODEL_3D_TASK_PARAMETERS.faceCount;
    if (parameter.name === 'enablePBR') return DEFAULT_MODEL_3D_TASK_PARAMETERS.enablePBR;
    if (isModel3DBooleanParameter(parameter)) return false;
    if (parameter.allowedValues.length > 0) return parameter.allowedValues[0];
    if (isModel3DNumericParameter(parameter) && parameter.minValue !== null && parameter.minValue !== undefined) return parameter.minValue;

    return parameter.isRequired ? '' : undefined;
}

export function getModel3DParameterValuesWithDefaults(parameters: readonly ModelGeneration3DParameter[], values: Model3DParameterValues | undefined) {
    return parameters.reduce<Model3DParameterValues>((acc, parameter) => {
        const value = values?.[parameter.name];
        const defaultValue = getDefaultModel3DParameterValue(parameter);
        if (value !== undefined) {
            acc[parameter.name] = value;
        } else if (defaultValue !== undefined) {
            acc[parameter.name] = defaultValue;
        }
        return acc;
    }, {});
}

export function getModel3DTaskParameterInput(
    parameters: readonly ModelGeneration3DParameter[],
    values: Model3DParameterValues | undefined
): Pick<ModelGeneration3DInput, 'faceCount' | 'enablePBR'> & Partial<ModelGeneration3DInput> {
    const valuesWithDefaults = getModel3DParameterValuesWithDefaults(parameters, values);
    const input: Record<string, unknown> = { ...DEFAULT_MODEL_3D_TASK_PARAMETERS };

    parameters.forEach((parameter) => {
        const value = coerceModel3DParameterValue(parameter, valuesWithDefaults[parameter.name]);
        if (value !== undefined) {
            input[parameter.name] = value;
        }
    });

    return input as Pick<ModelGeneration3DInput, 'faceCount' | 'enablePBR'> & Partial<ModelGeneration3DInput>;
}
